import React, { useEffect, useRef, useMemo } from "react";
import {
  Area,
  Bath,
  Bed,
  NewSparkle,
  Pricetag,
  Promote,
  VerifiedBadge,
  Wifi,
  Parking,
  Pool,
} from "@/components/bnp-icons";
import { formatNumber } from "@/utils/formatNumber";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter";
import moment from "moment";
import { safeMoment } from "@/utils/safeMoment";
import { useCurrency } from "@/hooks/useCurrency";

import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { getPropertyType } from "@/components/pages/user/dashboard/properties/Rent";
import MediaPreview from "@/components/bnp-ui/image";
import { PropertyService } from "@/services";
import { realtorNameOutput } from "@/utils/helpers/outputUserName";

export interface UnifiedPropertyCardProps {
  id: string;
  address: string;
  realtor?: any;
  description: string;
  status: string;
  listing_type: string;
  price: string | number;
  property_type: string;
  city?: string | null;
  state?: string | null;
  currency?: string;
  is_negotiable?: boolean;
  serviced?: boolean | null;
  shared?: boolean | null;
  furnished?: boolean | null;
  bedroom_count?: number | null;
  bathroom_count?: number | null;
  number_of_plots?: number | null;
  number_of_days?: number | null;
  amenities?: string[] | null;
  total_package?: string | number;
  promote_to_weekly_deals?: boolean;
  image_url: string[];
  created_at: string;
  property_stage?: string;
  additional_info?: string | null;
  agency_fees?: string | number;
  legal_fees?: string | number;
  caution_fee?: string | number | null;
  currentSlide?: number;
  totalSlides?: number;
  property_category?: string;
}

const UnifiedPropertyCard: React.FC<UnifiedPropertyCardProps> = ({
  id,
  address,
  realtor,
  description,
  status,
  listing_type,
  price,
  property_type,
  city,
  state,
  currency = "NGN",
  is_negotiable = false,
  serviced = false,
  shared = false,
  furnished = false,
  bedroom_count,
  bathroom_count,
  number_of_plots,
  number_of_days,
  amenities,
  total_package,
  promote_to_weekly_deals = false,
  image_url,
  created_at,
  property_stage,
  additional_info,
  agency_fees,
  legal_fees,
  caution_fee,
  currentSlide = 0,
  totalSlides = 1,
  property_category,
}) => {
  const isNewListing = moment().diff(safeMoment(created_at), "days") < 7;
  const isSponsored =
    realtor?.plan === "PLATINUM" || realtor?.plan === "DIAMOND";

  const isPartnerRealtor = realtor?.is_partner === true;

  const cardRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          if (entry.isIntersecting) {
            PropertyService.TrackPropertyInsight(id, "impression");
            observer.disconnect();
          }
        },
        {
          threshold: 0.5,
        },
      );

      observer.observe(node);
    },
    [id],
  );

  const { formatPrice } = useCurrency();

  const formattedPrice = useMemo(() => {
    return formatPrice(price, currency);
  }, [price, currency, formatPrice]);

  const formattedTotalPackage = useMemo(() => {
    if (!total_package) return null;
    return formatPrice(total_package, currency);
  }, [total_package, currency, formatPrice]);

  const getListingLabel = () => {
    switch (listing_type) {
      case "sale":
        return "Sale";
      case "rent":
        return "Rent";
      case "shortlet":
        return "Shortlet";
      case "land":
        return "Land";
      default:
        return capitalizeFirstLetter(listing_type);
    }
  };

  const renderAmenityIcon = (amenity: string) => {
    const lower = amenity.toLowerCase();
    if (lower === "wifi") return <Wifi key={amenity} />;
    if (lower === "parking") return <Parking key={amenity} />;
    return <Pool key={amenity} />;
  };

  const visibleAmenities = amenities?.slice(0, 3);
  const remainingAmenities = (amenities?.length || 0) - 3;

  return (
    <div
      ref={cardRef}
      className="relative font-sans text-base w-full sm:w-[350px] min-w-[300px] max-w-[350px] min-h-[561px] bg-white rounded-lg shadow-lg overflow-hidden"
    >
      {/* Top Badges */}
      <div className="absolute z-10 flex justify-between p-4 w-full sm:w-[350px]">
        <div className="flex justify-center font-bold items-center gap-2 bg-base-white text-bnp-dark p-2 sm:p-2 lg:p-0 text-sm min-w-[100px] min-h-[44px] sm:min-h-[28px] lg:w-[100px] lg:h-[28px] rounded-lg touch-manipulation active:scale-95 transition-transform">
          <Pricetag />
          <span className="font-bold">{getListingLabel()}</span>
        </div>
        <div className="flex flex-col gap-2">
          {isSponsored && (
            <div className="flex justify-center items-center gap-2 bg-purple-600 p-2 lg:p-0 text-sm text-white rounded-lg min-w-[123px] min-h-[44px] sm:min-h-[28px] lg:w-[123px] lg:h-[28px] touch-manipulation">
              <span className="font-bold">
                {realtor?.plan === "PLATINUM"
                  ? "Platinum Listing"
                  : realtor?.plan === "DIAMOND"
                    ? "Diamond Listing"
                    : ""}
              </span>
            </div>
          )}
          {promote_to_weekly_deals ? (
            <div className="flex justify-center items-center gap-2 bg-red-base p-2 lg:p-0 text-sm text-white rounded-lg min-w-[123px] min-h-[44px] sm:min-h-[28px] lg:w-[123px] lg:h-[28px] touch-manipulation active:scale-95 transition-transform">
              <Promote />
              <span className="font-bold">Deals</span>
            </div>
          ) : (
            isNewListing && (
              <div className="flex justify-center items-center gap-2 bg-bnp-dark p-2 lg:p-0 text-sm text-white rounded-lg min-w-[123px] min-h-[44px] sm:min-h-[28px] lg:w-[123px] lg:h-[28px] touch-manipulation">
                <NewSparkle />
                <span className="font-bold">New Listing</span>
              </div>
            )
          )}
        </div>
      </div>

      {/* Media */}
      <MediaPreview
        src={image_url}
        alt={description}
        className="w-full h-[200px] sm:h-[220px] object-cover"
      />

      {/* Content */}
      <div className="relative -mt-5 w-[95%] ml-auto mr-auto">
        <div className="bg-white rounded-t-xl">
          <div className="py-2 px-3">
            {/* Specs Row */}
            <div className="w-full flex items-center text-sm font-bold gap-2 sm:gap-4 text-soft-400 mb-4 border-b border-soft-200 pb-2">
              {(listing_type === "sale" ||
                listing_type === "rent" ||
                listing_type === "shortlet") && (
                <>
                  {bedroom_count != null && (
                    <div className="flex items-center gap-2 min-h-[44px] sm:min-h-0">
                      <Bed />
                      <span>{bedroom_count}</span>
                    </div>
                  )}
                  {bedroom_count != null && bathroom_count != null && (
                    <div className="border-r border-grey-5 h-4"></div>
                  )}
                  {bathroom_count != null && (
                    <div className="flex items-center gap-2 min-h-[44px] sm:min-h-0">
                      <Bath />
                      <span>{bathroom_count}</span>
                    </div>
                  )}
                </>
              )}
              {listing_type === "land" && number_of_plots != null && (
                <div className="flex items-center gap-2">
                  <Area />
                  <span>
                    {number_of_plots} plot{number_of_plots === 1 ? "" : "s"}
                  </span>
                </div>
              )}
              {(serviced || shared) && (
                <>
                  <div className="border-r border-grey-5 h-4"></div>
                  {serviced && (
                    <div className="flex gap-2 min-h-[44px] sm:min-h-0 items-center">
                      <p className="text-sm">Serviced</p>
                    </div>
                  )}
                  {shared && (
                    <div className="flex gap-2 min-h-[44px] sm:min-h-0 items-center">
                      <p className="text-sm">Shared</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Title */}
            <h2 className="text-base sm:text-lg font-bold text-dark-blue mb-4 line-clamp-1">
              {capitalizeFirstLetter(description)}
            </h2>

            {/* Price & Time */}
            <div className="flex flex-row justify-between items-start gap-2 mb-2">
              <div className="flex flex-col gap-1">
                <div className="text-grey-4 text-sm">
                  {getListingLabel()}
                  {is_negotiable && (
                    <span className="ml-1 text-[10px] text-green-primary font-medium">
                      Negotiable
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-lg sm:text-xl font-bold text-bnp-cyan leading-snug">
                    {formattedPrice}
                  </span>
                </div>
              </div>

              <span className="px-4 py-1 mt-1 bg-bnp-cyan/15 text-bnp-cyan text-xs rounded-lg font-bold flex items-center whitespace-nowrap">
                {safeMoment(created_at)?.fromNow() ?? "-"}
              </span>
            </div>

            {/* Details */}
            <div className="space-y-1 sm:space-y-4 bg-gray-50 p-3 sm:p-4 rounded-lg text-sm min-h-[168px]">
              {formattedTotalPackage && (
                <div className="flex justify-between py-1 sm:py-0 items-center">
                  <span className="text-grey-4 font-medium">Total Package</span>
                  <span className="font-bold text-dark-blue">
                    {formattedTotalPackage}
                  </span>
                </div>
              )}

              <div className="flex justify-between py-1 sm:py-0 items-center">
                <span className="text-grey-4 font-medium">Listed By</span>
                <div className="flex">
                  {isPartnerRealtor && <VerifiedBadge />}
                  <span className="font-bold text-dark-blue max-w-[150px] truncate">
                    {realtorNameOutput(
                      realtor?.business_name,
                      realtor?.first_name,
                      realtor?.last_name,
                    )}
                  </span>
                </div>
              </div>

              {property_type ? (
                <div className="flex justify-between py-1 sm:py-0 items-center">
                  <span className="text-grey-4 font-medium">Property Type</span>
                  <span className="font-bold text-dark-blue">
                    {getPropertyType(property_type)}
                  </span>
                </div>
              ) : property_category === "LAND" && number_of_plots != null ? (
                <div className="flex justify-between py-1 sm:py-0 items-center">
                  <span className="text-grey-4 font-medium">
                    Number of Plots
                  </span>
                  <span className="font-bold text-dark-blue">
                    {number_of_plots} plot{number_of_plots === 1 ? "" : "s"}
                  </span>
                </div>
              ) : null}

              <div className="flex justify-between py-1 sm:py-0 items-center">
                <span className="text-grey-4 font-medium">Location</span>
                <span className="font-bold text-dark-blue max-w-[150px] truncate">
                  {address}
                </span>
              </div>

              {listing_type === "rent" && (
                <div className="flex justify-between py-1 sm:py-0 items-center">
                  <span className="text-grey-4 font-medium">Furnished</span>
                  <span className="font-bold text-dark-blue">
                    {furnished ? "Yes" : "No"}
                  </span>
                </div>
              )}

              {listing_type === "shortlet" && number_of_days != null && (
                <div className="flex justify-between py-1 sm:py-0 items-center">
                  <span className="text-grey-4 font-medium">Minimum Days</span>
                  <span className="font-bold text-dark-blue">
                    {number_of_days}
                  </span>
                </div>
              )}

              {listing_type === "shortlet" &&
                visibleAmenities &&
                visibleAmenities.length > 0 && (
                  <div className="flex justify-between items-center gap-4">
                    <span className="text-grey-4 font-medium shrink-0">
                      Amenities
                    </span>
                    <div className="flex items-center gap-2 overflow-hidden">
                      {visibleAmenities.map((amenity: string) => (
                        <span
                          className="flex items-center gap-1 font-bold text-xs text-dark-blue whitespace-nowrap"
                          key={amenity}
                        >
                          {capitalizeFirstLetter(amenity)}
                          {renderAmenityIcon(amenity)}
                        </span>
                      ))}
                      {remainingAmenities > 0 && (
                        <span className="text-xs font-semibold text-grey-4 whitespace-nowrap">
                          +{remainingAmenities} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedPropertyCard;
